/**
 * PRD-26 §FR-19 — canonical kind → SPA route mapping.
 *
 * The backend writes a ``link`` field on every Notification row but
 * the SPA NEVER trusts it for navigation: this file computes the
 * target from ``kind`` + ``payload`` instead. Why:
 *   - Defends against a future bug where a backend writer sets an
 *     unsafe URL ("https://attacker.example/...") — the SPA only
 *     navigates to known relative paths.
 *   - Keeps route renames a single-file edit: when /materiales
 *     becomes /biblioteca, this is the only place that updates.
 *   - Lets the unit-tested mapping be the contract, not an English
 *     spec.
 *
 * Adding a new kind:
 *   1. Extend ``NotificationKind`` in src/types/index.ts
 *   2. Add a case here returning the target path
 *   3. Add the icon in icons.ts
 *   4. Add i18n keys in notifications.json
 */

import type { Notification, NotificationKind } from "@/types"

const asString = (v: unknown): string | null => (typeof v === "string" && v.length > 0 ? v : null)

export function notificationLink(notif: Notification): string {
  const p = notif.payload ?? {}
  const materialId = asString(p.material_id)
  const studentId = asString(p.student_id)
  const classroomId = asString(p.classroom_id)
  const kind = notif.kind as NotificationKind

  switch (kind) {
    case "material.ready":
    case "material.generation_failed":
    case "material.scheduled_dispatched":
    case "material.scheduled_failed":
    case "material.scheduled_auto_sent":
    case "material.deadline_approaching":
    case "material.roster_gap":
    case "material.send_failed":
    case "portal.student_uploaded":
      // `material.roster_gap` (PRD-42 §FR-7.1) belongs here: the
      // material's own page is where the professor sees which students
      // the send could not reach.
      return materialId ? `/materiales/${materialId}` : "/materiales"
    case "material.due_today":
    case "grading.completed_auto":
      // PRD-58 §FR-1.6 — the material page owns its results now (the
      // standalone gradebook is gone), so "due today" and "we graded it
      // for you" both land on the material's Resultados block.
      return materialId ? `/materiales/${materialId}` : "/materiales"
    case "material.review_ready":
    case "grading.review_needed":
      // PRD-41 §FR-5.3 / §FR-6, re-homed by PRD-58 §FR-1.2 — the review
      // queue is the purpose-built surface for both: it opens on the
      // material's pending submissions with photo + suggested score side
      // by side. Deliberately NOT grouped with the `/materiales/:id` kinds
      // above: the professor's next action is confirming grades, not
      // re-reading the material. `material_id` is nullable on
      // `grading.review_needed` (a Task without a Material) and rows
      // created before PRD-26 may not carry it at all — those fall back
      // to the materials list rather than `/materiales/undefined/revision`.
      return materialId ? `/materiales/${materialId}/revision` : "/materiales"
    case "student.survey_completed":
      if (classroomId && studentId) return `/salones/${classroomId}/alumnos/${studentId}`
      if (classroomId) return `/salones/${classroomId}/alumnos`
      return "/salones"
    case "welcome":
      return "/"
    default:
      // Server-supplied path as a last-resort fallback. Only used when
      // we receive a kind the client doesn't recognize yet (e.g. an
      // older client + a newer backend). The fallback is still a path
      // string — never an external URL.
      return notif.link?.startsWith("/") ? notif.link : "/notificaciones"
  }
}
